import { type ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { BaseInput, CodeEntrySlots } from '../../../shared/ui';
import { type VerifyCodeState } from '../model/use-verify-form';

import { VerifyPasteCodeAction } from './verify-paste-code-action';

type VerifyCodeEntryProps = {
  code: string;
  codeState: VerifyCodeState;
  codeSlots: ReadonlyArray<string>;
  isInputLocked: boolean;
  onPasteCodeFromClipboard: () => Promise<void>;
  onCodeChange: (value: string) => void;
};

const VERIFY_CODE_GROUP_BREAK_AFTER = 2;

const codeEntryErrorShake = keyframes`
  0%,
  100% {
    transform: translateX(0);
  }

  20% {
    transform: translateX(-6px);
  }

  40% {
    transform: translateX(6px);
  }

  60% {
    transform: translateX(-4px);
  }

  80% {
    transform: translateX(4px);
  }
`;

const CodeEntryPanel = styled.div.attrs({
  'data-ui': 'auth-verify-code-entry',
})<{ $codeState: VerifyCodeState }>`
  position: relative;
  width: ${({ theme }) => theme.scale.px(331)};
  min-height: ${({ theme }) => theme.scale.px(57)};
  align-self: center;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.surface.subtle};
  border: ${({ $codeState, theme }) => {
    if ($codeState === 'success') {
      return `${theme.scale.px(2)} solid ${theme.colors.support.success}`;
    }

    if ($codeState === 'error') {
      return `${theme.scale.px(2)} solid ${theme.colors.support.danger}`;
    }

    return `${theme.scale.px(1)} solid ${theme.colors.border.subtle}`;
  }};
  transition: background-color 160ms ease, box-shadow 160ms ease;
  animation: ${({ $codeState }) =>
      $codeState === 'error' ? codeEntryErrorShake : 'none'}
    320ms ease;

  &:focus-within {
    background: ${({ theme }) => theme.colors.surface.overlay};
    box-shadow: ${({ $codeState, theme }) =>
      $codeState === 'idle'
        ? `0 0 0 ${theme.scale.px(2)} ${theme.colors.brand.primary}`
        : 'none'};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HiddenCodeInput = styled(BaseInput)`
  position: absolute;
  width: ${({ theme }) => theme.scale.px(1)};
  height: ${({ theme }) => theme.scale.px(1)};
  padding: 0;
  margin: ${({ theme }) => theme.scale.px(-1)};
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const PasteCodeActionSlot = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.scale.px(16)};
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
`;

export function VerifyCodeEntry({
  code,
  codeState,
  codeSlots,
  isInputLocked,
  onPasteCodeFromClipboard,
  onCodeChange,
}: VerifyCodeEntryProps) {
  const { t } = useTranslation();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const focusInput = () => {
    if (isInputLocked) {
      return;
    }

    codeInputRef.current?.focus();
  };

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    onCodeChange(event.target.value);
  };

  return (
    <CodeEntryPanel $codeState={codeState} onClick={focusInput}>
      <HiddenCodeInput
        ref={codeInputRef}
        aria-label={t('verify.codeAria')}
        autoComplete="one-time-code"
        disabled={isInputLocked}
        inputMode="numeric"
        value={code}
        onChange={handleCodeChange}
      />

      <CodeEntrySlots
        code={code}
        codeLength={codeSlots.length}
        groupBreakAfter={VERIFY_CODE_GROUP_BREAK_AFTER}
      />

      <PasteCodeActionSlot>
        <VerifyPasteCodeAction
          ariaLabel={t('verify.pasteCodeAria')}
          isDisabled={isInputLocked}
          onPasteCode={onPasteCodeFromClipboard}
        />
      </PasteCodeActionSlot>
    </CodeEntryPanel>
  );
}
